<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import PillSelect from '@/modules/shared/components/pill-select.vue'
import { VTextField } from '@/modules/shared/components'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'
import { useWorkspaceOnboardingStore } from '../../stores/workspace-onboarding-store'

const candidateStore = useCandidateStore()
const onboardingStore = useWorkspaceOnboardingStore()

const INDUSTRIES = [
  'AgTech',
  'AI / ML',
  'Biotech',
  'Climate',
  'Consumer',
  'Crypto / Web3',
  'Cybersecurity',
  'Deep Tech',
  'EdTech',
  'Enterprise',
  'Fintech',
  'Gaming',
  'Hardware',
  'Health Tech',
  'InsurTech',
  'Logistics',
  'Marketplace',
  'Media',
  'Real Estate',
  'SaaS',
  'Other...',
]

const form = ref({ industries_interested: [] as string[] })
const otherText = ref('')

const isOtherSelected = computed(() => form.value.industries_interested.includes('Other...'))

const candidate = computed(() => {
  const id = onboardingStore.workspace?.connecting_candidate_id
  return id ? candidateStore.getCandidate(id.toString()) : undefined
})

watch(
  candidate,
  (c) => {
    if (!c) return
    const industries = c.industries_interested || []
    form.value.industries_interested = industries

    // If there's a value that's not in the predefined list, treat it as the "Other" text
    const custom = industries.find((i: string) => !INDUSTRIES.includes(i) && i !== 'Other...')
    if (custom) {
      otherText.value = custom
      if (!industries.includes('Other...')) {
        form.value.industries_interested.push('Other...')
      }
    }
  },
  { immediate: true },
)

// When "Other" is deselected, clear the text and remove any custom values from the list
watch(isOtherSelected, (selected) => {
  if (!selected) {
    otherText.value = ''
    form.value.industries_interested = form.value.industries_interested.filter((i) => INDUSTRIES.includes(i))
  }
})

const submit = async (): Promise<boolean> => {
  const candidateId = onboardingStore.workspace?.connecting_candidate_id
  if (!candidateId) return false

  // Build the final list: predefined selections (excluding "Other" label) + custom text if provided
  let industries = form.value.industries_interested.filter((i) => i !== 'Other...')
  if (isOtherSelected.value && otherText.value.trim()) {
    industries.push(otherText.value.trim())
  }

  await candidateStore.updateCandidate({
    id: candidateId,
    subject_id: onboardingStore.workspace!.id,
    subject_type: 'Group',
    industries_interested: industries,
  })
  return true
}

defineExpose({ submit })
</script>

<template>
  <div class="flex flex-1 flex-col">
    <PillSelect v-model="form.industries_interested" :options="INDUSTRIES" />

    <div v-if="isOtherSelected" class="mt-4">
      <VTextField v-model="otherText" placeholder="Tell us more..." />
    </div>
  </div>
</template>
